import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AfterPopupHideIds, PopupBanner } from '../../interfaces/popup.interface';
import { DynamicLinkService } from '../../service/dynamic-link.service';
import { HtmlCtaService } from '../../service/html-cta.service';
import { LocalStorageService } from '../../service/localstorage.service';
import { isRichTextContent, replaceHrefValues } from '../../utils/rich-text.util';

@UntilDestroy()
@Component({
  selector: 'banner-popup',
  templateUrl: './banner-popup.component.html',
  styleUrls: ['./banner-popup.component.scss'],
})
export class BannerPopupComponent implements OnInit {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { bannerList: PopupBanner[]; closeCallback: () => void },
    private dialogRef: MatDialogRef<BannerPopupComponent>,
    private localStorageService: LocalStorageService,
    private router: Router,
    private dynamicLinkService: DynamicLinkService,
    private htmlCtaService: HtmlCtaService,
    private sanitizer: DomSanitizer,
  ) {}

  @Input() bannerList: PopupBanner[] = [];
  @Input() closeCallback: () => void = () => {};

  domain = window.location.origin;

  isH5!: boolean;
  isLoading: boolean = false;
  isShowAnimation: boolean = false;
  isLogin: boolean = false;
  isHide: boolean = false;
  curBannerIdx: number = 0;
  curBanner: PopupBanner = {
    id: 0,
    title: '',
    content: '',
    image: '',
    url: '',
  };

  /** 文案是否为富文本；不是的话是旧的纯文字，换行要靠 white-space: pre-line (ALL-13956) */
  isRichContent = false;

  /** 渲染用的文案 */
  contentHtml: SafeHtml = '';

  ngOnInit() {
    if (!this.data) return;

    this.bannerList = this.data.bannerList;
    this.closeCallback = this.data.closeCallback;
    this.isLogin = Boolean(this.localStorageService.loginToken);

    if (this.bannerList.length > 0) {
      this.setCurBanner(this.bannerList[this.curBannerIdx]);
    }
  }

  goToLink() {
    if (this.curBanner.url) {
      // ${domain} 动态链接交给 HtmlCtaService 解析；其他网址维持原本的行为 (ALL-13956)
      if (this.dynamicLinkService.hasDomainPlaceholder(this.curBanner.url)) {
        this.htmlCtaService.navigate(this.curBanner.url);
      } else {
        this.router.navigateByUrl(this.curBanner.url);
      }
      this.closeCallback();
      this.dialogRef.close();
    }
  }

  /**
   * 切换当前 Banner，并准备渲染用的文案 (ALL-13956)
   *
   * 后台弹窗文案改成富文本后，内容可能含 CTA 按钮（.cta-button + data-link）：
   * - CTA 的 data-link 保留 ${domain} 原样，点击时交给 HtmlCtaService 解析成站内路径
   * - 一般超链接只替换 href 里的 ${domain}
   * 旧的纯文字内容维持原本的处理：整段替换 ${domain}。
   */
  private setCurBanner(banner: PopupBanner) {
    this.curBanner = banner;
    this.isRichContent = isRichTextContent(banner?.content);

    const content = banner?.content || '';
    const html = this.isRichContent
      ? replaceHrefValues(this.htmlCtaService.transformCtaButtons(content), url =>
          this.dynamicLinkService.replaceDomainPlaceholder(url),
        )
      : this.dynamicLinkService.replaceDomainPlaceholder(content);
    this.contentHtml = this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /** 文案内的 CTA 按钮：跳转后关闭弹窗，跟点图片的行为一致 */
  onContentClick(event: Event) {
    if (!this.htmlCtaService.handleClick(event)) return;

    this.closeCallback();
    this.dialogRef.close();
  }

  onShowChange() {
    this.isHide = !this.isHide;
  }

  onClose() {
    this.isShowAnimation = true;
    this.curBannerIdx += 1;

    if (this.isHide) {
      if (this.isLogin) {
        const hideIds: AfterPopupHideIds[] = this.localStorageService.getValueByJsonParse('isHideAF') || [];
        const curUid = this.localStorageService.getValueByJsonParse('uid');
        const curHideIds = hideIds.find(item => item.uid === curUid)?.ids || [];

        if (!curHideIds.includes(this.curBanner.id)) {
          let newHideIds = [];
          if (hideIds.findIndex(item => item.uid === curUid) > -1) {
            // existed already
            newHideIds = hideIds.map(item => {
              if (item.uid === curUid) {
                return {
                  uid: curUid,
                  ids: [...item.ids, this.curBanner.id],
                };
              }
              return item;
            });
          } else {
            // new user
            newHideIds = [...hideIds, { uid: curUid, ids: [this.curBanner.id] }];
          }

          this.localStorageService.setValueByJsonStringify('isHideAF', newHideIds);
        }
      } else {
        const hideIds = this.localStorageService.getValueByJsonParse('isHideBE') || [];

        if (!hideIds.includes(this.curBanner.id)) {
          this.localStorageService.setValueByJsonStringify('isHideBE', [...hideIds, this.curBanner.id]);
        }
      }
    }

    if (this.curBannerIdx < this.bannerList.length) {
      // delay 400ms for data change
      setTimeout(() => {
        this.setCurBanner(this.bannerList[this.curBannerIdx]);
        this.isHide = false;
      }, 400);

      // delay 600ms for animation
      setTimeout(() => {
        this.isShowAnimation = false;
      }, 600);
    } else {
      this.closeCallback();
      this.dialogRef.close();
    }
  }
}
